import type { CSSProperties } from 'react';

// Shared styles for all auth pages (login, register, reset password)
// Matches SmartBloček design language

export const authStyles = {
    page: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    } as CSSProperties,

    card: {
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '24px',
        padding: '40px 32px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        width: '100%',
        maxWidth: '420px',
        boxSizing: 'border-box',
    } as CSSProperties,

    logo: {
        textAlign: 'center',
        marginBottom: '24px',
    } as CSSProperties,

    logoIcon: {
        fontSize: '40px',
        marginBottom: '8px',
    } as CSSProperties,

    logoTitle: {
        fontSize: '28px',
        fontWeight: 700,
        color: '#2d3748',
        margin: 0,
        letterSpacing: '-0.5px',
    } as CSSProperties,

    subtitle: {
        fontSize: '15px',
        color: '#64748b',
        marginTop: '8px',
        marginBottom: '0',
        lineHeight: '1.4',
    } as CSSProperties,

    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    } as CSSProperties,

    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    } as CSSProperties,

    label: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#374151',
    } as CSSProperties,

    input: {
        width: '100%',
        padding: '14px 16px',
        border: '2px solid #e2e8f0',
        borderRadius: '12px',
        fontSize: '15px',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        outline: 'none',
        backgroundColor: '#fff',
    } as CSSProperties,

    inputFocused: {
        borderColor: '#667eea',
        boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.15)',
    } as CSSProperties,

    inputError: {
        borderColor: '#ef4444',
        boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
    } as CSSProperties,

    passwordWrapper: {
        position: 'relative',
    } as CSSProperties,

    passwordToggle: {
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 500,
        color: '#667eea',
        padding: '4px 6px',
    } as CSSProperties,

    button: {
        width: '100%',
        padding: '14px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
        border: 'none',
        borderRadius: '12px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
    } as CSSProperties,

    buttonDisabled: {
        opacity: 0.6,
        cursor: 'not-allowed',
    } as CSSProperties,

    googleButton: {
        width: '100%',
        padding: '14px',
        background: '#fff',
        color: '#374151',
        border: '2px solid #e2e8f0',
        borderRadius: '12px',
        fontSize: '15px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
    } as CSSProperties,

    divider: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        margin: '4px 0',
    } as CSSProperties,

    dividerLine: {
        flex: 1,
        height: '1px',
        background: '#e2e8f0',
    } as CSSProperties,

    dividerText: {
        fontSize: '13px',
        color: '#94a3b8',
        fontWeight: 500,
    } as CSSProperties,

    error: {
        padding: '12px 16px',
        background: '#fef2f2',
        color: '#dc2626',
        borderRadius: '10px',
        fontSize: '14px',
        lineHeight: '1.5',
        border: '1px solid #fecaca',
    } as CSSProperties,

    info: {
        padding: '12px 16px',
        background: '#eff6ff',
        color: '#1d4ed8',
        borderRadius: '10px',
        fontSize: '14px',
        lineHeight: '1.5',
        border: '1px solid #bfdbfe',
    } as CSSProperties,

    success: {
        padding: '12px 16px',
        background: '#f0fdf4',
        color: '#16a34a',
        borderRadius: '10px',
        fontSize: '14px',
        lineHeight: '1.5',
        border: '1px solid #bbf7d0',
    } as CSSProperties,

    link: {
        color: '#667eea',
        textDecoration: 'none',
        fontWeight: 500,
        fontSize: '14px',
    } as CSSProperties,

    footer: {
        textAlign: 'center',
        marginTop: '20px',
        fontSize: '14px',
        color: '#64748b',
    } as CSSProperties,

    stepIndicator: {
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        marginBottom: '20px',
    } as CSSProperties,

    stepDot: (active: boolean, completed: boolean): CSSProperties => ({
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        background: completed ? '#667eea' : active ? '#667eea' : '#e2e8f0',
        opacity: active || completed ? 1 : 0.5,
        transition: 'all 0.3s',
    }),

    fieldError: {
        fontSize: '12px',
        color: '#ef4444',
        marginTop: '2px',
    } as CSSProperties,
};

// Input sanitization - strip HTML tags and limit length
export function sanitizeInput(value: string, maxLength: number = 254): string {
    return value
        .replace(/<[^>]*>/g, '') // strip HTML tags
        .replace(/[<>"'`;]/g, '') // strip dangerous characters
        .slice(0, maxLength);
}

// Email validation
export function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 254;
}
